import { useCallback, useMemo, useRef } from "react";

import { newIdempotencyKey } from "./client";

/**
 * Idempotency-Key per form submission (XC-08).
 *
 * `keyFor(payload)` returns the same key while the payload stays the same — so a retry or a double
 * click sends one key and the API runs the submission once — and a fresh key as soon as the payload
 * changes (an edited form is a new submission, never a "key reused with a different body" error).
 * Call `reset()` after a success so the next submission starts afresh. The payload only needs to
 * identify the submission: pass the JSON body, or a small description of a file upload.
 */
export function useIdempotencyKey(): { keyFor: (payload?: unknown) => string; reset: () => void } {
  const ref = useRef<{ key: string; sig?: string }>({ key: newIdempotencyKey() });
  const keyFor = useCallback((payload?: unknown) => {
    const sig = payload === undefined ? undefined : JSON.stringify(payload);
    if (sig !== undefined && ref.current.sig !== undefined && sig !== ref.current.sig) {
      ref.current = { key: newIdempotencyKey(), sig };
    } else if (sig !== undefined) {
      ref.current.sig = sig;
    }
    return ref.current.key;
  }, []);
  const reset = useCallback(() => {
    ref.current = { key: newIdempotencyKey() };
  }, []);
  return useMemo(() => ({ keyFor, reset }), [keyFor, reset]);
}
